import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { questionsAPI, myResponsesAPI, myQuestionsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import PrivacyReminder from '@/components/PrivacyReminder';
import { 
  CheckCircle, 
  ChevronRight,
  FileQuestion,
  Plus,
  X,
  GripVertical,
  Target,
  CheckCircle2,
  Loader2,
  SkipForward,
  Ban
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export default function AboutMePage() {
  const { projectId } = useParams();
  const { currentProject, drivers } = useProject();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [activeDriverId, setActiveDriverId] = useState(null);
  const [topicInputs, setTopicInputs] = useState({}); // { questionId: inputValue }
  const [dragState, setDragState] = useState({ questionId: null, index: null });
  const [skipPopoverOpen, setSkipPopoverOpen] = useState({}); // { questionId: boolean }
  const saveTimeoutRef = React.useRef(null);

  // Skip reasons for questions
  const SKIP_REASONS = [
    { value: 'not_applicable', label: 'Not applicable to me' },
    { value: 'insufficient_info', label: "I don't have enough information" },
    { value: 'prefer_not_answer', label: 'Prefer not to answer' },
    { value: 'unsure', label: "I'm unsure / Don't know" },
    { value: 'other', label: 'Other reason' },
  ];

  useEffect(() => {
    const fetchQuestionsAndResponses = async () => {
      if (!projectId) return;
      
      try {
        // Fetch questions filtered by user's segments
        const myQuestionsResponse = await myQuestionsAPI.get(projectId, 'about_me');
        const questionsList = myQuestionsResponse.data.questions || [];
        setQuestions(questionsList);
        
        // Initialize default responses
        const initialResponses = {};
        questionsList.forEach(q => {
          if (q.question_type === 'checkbox' || q.question_type === 'multi_topic') {
            initialResponses[q.id] = [];
          } else if (q.question_type === 'slider' || q.question_type === 'rating') {
            initialResponses[q.id] = null; // null until user answers
          } else {
            initialResponses[q.id] = '';
          }
        });
        
        // Fetch existing responses and merge with defaults
        try {
          const savedResponsesResult = await myResponsesAPI.get(projectId);
          const savedResponses = savedResponsesResult.data.responses || {};
          
          // Merge saved responses with defaults, normalizing formats
          Object.keys(savedResponses).forEach(questionId => {
            if (initialResponses.hasOwnProperty(questionId)) {
              let val = savedResponses[questionId];
              // Normalize dict-style multi-topic {topic: true} to array [topic]
              if (val && typeof val === 'object' && !Array.isArray(val) && !val.skipped) {
                val = Object.keys(val).filter(k => val[k]);
              }
              initialResponses[questionId] = val;
            }
          });
        } catch (err) {
          console.log('No existing responses found or error fetching:', err);
        }
        
        setResponses(initialResponses);
      } catch (err) {
        console.error('Error fetching questions:', err);
        toast.error('Failed to load questions');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionsAndResponses();
  }, [projectId]);

  // Set first driver with questions as active when drivers or questions load
  useEffect(() => {
    if (loading || questions.length === 0) return;
    const withQuestions = drivers.filter(d => questions.some(q => q.driver_id === d.id));
    if (withQuestions.length > 0 && (!activeDriverId || !withQuestions.some(d => d.id === activeDriverId))) {
      setActiveDriverId(withQuestions[0].id);
    } else if (withQuestions.length === 0 && !activeDriverId) {
      // Fallback: check for questions without a driver (general questions)
      const generalQs = questions.filter(q => !q.driver_id);
      if (generalQs.length > 0) {
        setActiveDriverId('general');
      }
    }
  }, [drivers, activeDriverId, questions, loading]);

  // Auto-save responses when they change (debounced)
  useEffect(() => {
    // Don't save if no responses yet or still loading
    if (loading || Object.keys(responses).length === 0) return;
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce the save (wait 1 second after last change)
    saveTimeoutRef.current = setTimeout(async () => {
      await saveResponses();
    }, 1000);
    
    // Cleanup on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [responses]);

  // Save responses to backend (immediate save)
  const saveResponses = async (showToast = false) => {
    // Cancel any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    setSaving(true);
    try {
      // Convert responses object to array format for API
      const responsesArray = Object.entries(responses).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer
      }));
      
      await myResponsesAPI.save(projectId, responsesArray);
      setLastSaved(new Date());
      if (showToast) {
        toast.success('Responses saved');
      }
    } catch (err) {
      console.error('Auto-save failed:', err);
      toast.error('Failed to save responses');
    } finally {
      setSaving(false);
    }
  };

  // Get questions grouped by driver
  const getQuestionsForDriver = (driverId) => {
    return questions.filter(q => q.driver_id === driverId).sort((a, b) => a.order - b.order);
  };

  // Check if a response is considered "answered" (including skipped)
  const isQuestionAnswered = (questionId, answer, questionType) => {
    // Check if question was skipped
    if (answer && typeof answer === 'object' && answer.skipped === true) {
      return true;
    }
    
    if (questionType === 'checkbox' || questionType === 'multi_topic') {
      return answer && answer.length > 0;
    }
    if (questionType === 'slider' || questionType === 'rating') {
      return answer !== null && answer !== undefined; // Only answered if user set a value
    }
    return answer && answer.toString().trim() !== '';
  };

  // Calculate progress for a driver (including "General" driver)
  const getDriverProgress = (driverId) => {
    // Handle General driver specially
    const GENERAL_DRIVER_ID = '__general__';
    const driverQuestions = driverId === GENERAL_DRIVER_ID 
      ? questions.filter(q => !q.driver_id).sort((a, b) => a.order - b.order)
      : getQuestionsForDriver(driverId);
    if (driverQuestions.length === 0) return { answered: 0, total: 0, percent: 0 };
    
    const answered = driverQuestions.filter(q => 
      isQuestionAnswered(q.id, responses[q.id], q.question_type)
    ).length;
    
    return {
      answered,
      total: driverQuestions.length,
      percent: Math.round((answered / driverQuestions.length) * 100)
    };
  };

  // Calculate overall progress
  const getOverallProgress = () => {
    if (questions.length === 0) return { answered: 0, total: 0, percent: 0 };
    
    const answered = questions.filter(q => 
      isQuestionAnswered(q.id, responses[q.id], q.question_type)
    ).length;
    
    return {
      answered,
      total: questions.length,
      percent: Math.round((answered / questions.length) * 100)
    };
  };

  const updateResponse = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const toggleCheckbox = (questionId, option) => {
    const current = responses[questionId] || [];
    const updated = current.includes(option)
      ? current.filter(o => o !== option)
      : [...current, option];
    updateResponse(questionId, updated);
  };

  // Skip question functions
  const handleSkipQuestion = (questionId, reason) => {
    updateResponse(questionId, { skipped: true, reason: reason });
    setSkipPopoverOpen(prev => ({ ...prev, [questionId]: false }));
    toast.success('Question skipped');
  };

  const handleUnskipQuestion = (questionId, questionType) => {
    // Reset to default value based on question type
    let defaultValue;
    if (questionType === 'checkbox' || questionType === 'multi_topic') {
      defaultValue = [];
    } else if (questionType === 'slider' || questionType === 'rating') {
      defaultValue = null; // Reset to unanswered
    } else {
      defaultValue = '';
    }
    updateResponse(questionId, defaultValue);
    toast.success('Question restored');
  };

  const isQuestionSkipped = (questionId) => {
    const answer = responses[questionId];
    return answer && typeof answer === 'object' && answer.skipped === true;
  };

  const getSkipReason = (questionId) => {
    const answer = responses[questionId];
    if (answer && typeof answer === 'object' && answer.skipped === true) {
      return SKIP_REASONS.find(r => r.value === answer.reason)?.label || answer.reason;
    }
    return null;
  };

  // Multi-topic functions
  const addTopic = (questionId) => {
    const input = topicInputs[questionId]?.trim();
    if (!input) return;
    const current = responses[questionId] || [];
    updateResponse(questionId, [...current, input]);
    setTopicInputs(prev => ({ ...prev, [questionId]: '' }));
  };

  const removeTopic = (questionId, index) => {
    const current = responses[questionId] || [];
    updateResponse(questionId, current.filter((_, i) => i !== index));
  };

  const handleTopicDragStart = (questionId, index) => {
    setDragState({ questionId, index });
  };

  const handleTopicDragOver = (e, questionId, index) => {
    e.preventDefault();
    if (dragState.questionId !== questionId || dragState.index === null || dragState.index === index) return;
    
    const current = [...(responses[questionId] || [])];
    const draggedItem = current[dragState.index];
    current.splice(dragState.index, 1);
    current.splice(index, 0, draggedItem);
    updateResponse(questionId, current);
    setDragState({ questionId, index });
  };

  const handleTopicDragEnd = () => {
    setDragState({ questionId: null, index: null });
  };

  // Touch-based drag and drop for mobile
  const touchState = React.useRef({ startY: 0, currentIndex: null, questionId: null, isDragging: false });
  
  const handleTouchStart = (e, questionId, index) => {
    const touch = e.touches[0];
    touchState.current = {
      startY: touch.clientY,
      currentIndex: index,
      questionId,
      isDragging: false,
      startX: touch.clientX,
    };
    setDragState({ questionId, index });
  };

  const handleTouchMove = (e, questionId) => {
    if (touchState.current.questionId !== questionId) return;
    
    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - touchState.current.startY);
    const deltaX = Math.abs(touch.clientX - touchState.current.startX);
    
    // If horizontal movement is greater, it's likely a scroll attempt - allow it
    if (deltaX > deltaY && !touchState.current.isDragging) return;
    
    // Start dragging after 10px vertical movement
    if (deltaY > 10) {
      touchState.current.isDragging = true;
      e.preventDefault(); // Prevent scrolling only when dragging
    }
    
    if (!touchState.current.isDragging) return;
    
    // Find which item we're over
    const container = e.currentTarget.closest('[data-topic-container]');
    if (!container) return;
    
    const items = container.querySelectorAll('[data-topic-item]');
    const touchY = touch.clientY;
    
    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      
      if (touchY < midY && i !== touchState.current.currentIndex) {
        // Reorder items
        const current = [...(responses[questionId] || [])];
        const draggedItem = current[touchState.current.currentIndex];
        current.splice(touchState.current.currentIndex, 1);
        current.splice(i, 0, draggedItem);
        updateResponse(questionId, current);
        touchState.current.currentIndex = i;
        setDragState({ questionId, index: i });
        break;
      } else if (touchY > midY && touchY < rect.bottom && i !== touchState.current.currentIndex && i === items.length - 1) {
        // Handle dropping at the end
        const current = [...(responses[questionId] || [])];
        const draggedItem = current[touchState.current.currentIndex];
        current.splice(touchState.current.currentIndex, 1);
        current.push(draggedItem);
        updateResponse(questionId, current);
        touchState.current.currentIndex = current.length - 1;
        setDragState({ questionId, index: current.length - 1 });
        break;
      }
    }
  };

  const handleTouchEnd = () => {
    touchState.current = { startY: 0, currentIndex: null, questionId: null, isDragging: false };
    setDragState({ questionId: null, index: null });
  };

  // Get drivers that have questions (needed for navigation)
  // Include a "General" pseudo-driver for questions without a driver_id
  const questionsWithoutDriver = questions.filter(q => !q.driver_id);
  const GENERAL_DRIVER_ID = '__general__';
  const generalDriver = questionsWithoutDriver.length > 0 ? {
    id: GENERAL_DRIVER_ID,
    name: 'General',
    description: 'General questions',
    order: 9999 // Show at the end
  } : null;
  
  const driversWithQuestions = [
    ...drivers.filter(d => getQuestionsForDriver(d.id).length > 0),
    ...(generalDriver ? [generalDriver] : [])
  ];
  
  // Override getQuestionsForDriver to handle "General" driver
  const getQuestionsForDriverOrGeneral = (driverId) => {
    if (driverId === GENERAL_DRIVER_ID) {
      return questionsWithoutDriver.sort((a, b) => a.order - b.order);
    }
    return getQuestionsForDriver(driverId);
  };

  // Get the index of the current active driver
  const getActiveDriverIndex = () => {
    return driversWithQuestions.findIndex(d => d.id === activeDriverId);
  };

  // Check if current driver is the last one
  const isLastDriver = () => {
    const idx = getActiveDriverIndex();
    return idx === driversWithQuestions.length - 1;
  };

  // Navigate to next driver or complete survey
  const handleNextDriver = async () => {
    // Save responses before navigating
    await saveResponses(false);
    
    const currentIdx = getActiveDriverIndex();
    
    if (currentIdx < driversWithQuestions.length - 1) {
      // Move to next driver
      setActiveDriverId(driversWithQuestions[currentIdx + 1].id);
      // Scroll to top of questions
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Last driver - mark as completed
      setSubmitted(true);
      toast.success('Survey completed! Your responses have been saved.');
    }
  };

  // Handle driver tab click - save before switching
  const handleDriverTabClick = async (driverId) => {
    if (driverId === activeDriverId) return;
    
    // Save responses before switching
    await saveResponses(false);
    setActiveDriverId(driverId);
  };

  const overallProgress = getOverallProgress();

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            About Me
          </h1>
          <p className="text-slate-600 mt-1">Answer questions about yourself and your perspective</p>
        </div>
        <Card className="dashboard-card">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FileQuestion className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Questions Available</h3>
            <p className="text-slate-500">
              There are no questions assigned to you yet.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            About Me
          </h1>
        </div>
        <Card className="dashboard-card max-w-2xl mx-auto">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Thank You!</h3>
            <p className="text-slate-500 mb-6">
              Your responses have been saved successfully.
            </p>
            <Button onClick={() => setSubmitted(false)} variant="outline">
              Review Responses
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get active questions and driver
  const activeQuestions = activeDriverId ? getQuestionsForDriverOrGeneral(activeDriverId) : [];
  const activeDriver = activeDriverId === GENERAL_DRIVER_ID ? generalDriver : drivers.find(d => d.id === activeDriverId);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            About Me
          </h1>
          <p className="text-slate-600 mt-1">Answer questions about yourself and your perspective</p>
          <PrivacyReminder className="mt-2" />
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm text-slate-500">Overall Progress</p>
            <p className="text-lg font-semibold text-slate-900">
              {overallProgress.answered} / {overallProgress.total}
            </p>
          </div>
          <div className="w-16 h-16 relative">
            <svg className="w-16 h-16 transform -rotate-90">
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="#e2e8f0"
                strokeWidth="6"
                fill="none"
              />
              <circle
                cx="32"
                cy="32"
                r="28"
                stroke="#5AA2AE"
                strokeWidth="6"
                fill="none"
                strokeDasharray={`${overallProgress.percent * 1.76} 176`}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-slate-700">
              {overallProgress.percent}%
            </span>
          </div>
        </div>
      </div>

      {/* Driver Navigation Buttons - Horizontally scrollable on mobile */}
      <div className="overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-2 min-w-max">
          {driversWithQuestions.map((driver) => {
            const progress = getDriverProgress(driver.id);
            const isActive = driver.id === activeDriverId;
            const isComplete = progress.percent === 100;
            
            return (
              <Button
                key={driver.id}
                variant={isActive ? "default" : "outline"}
                className={`relative whitespace-nowrap ${isActive ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''}`}
                onClick={() => handleDriverTabClick(driver.id)}
                disabled={saving}
              >
                {isComplete ? (
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
                ) : (
                  <Target className="w-4 h-4 mr-2" />
                )}
                {driver.name}
                <Badge 
                  variant="secondary" 
                  className={`ml-2 text-xs ${isComplete ? 'bg-emerald-100 text-emerald-700' : ''}`}
                >
                  {progress.answered}/{progress.total}
                </Badge>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Active Driver Section */}
      {activeDriver && (
        <div className="space-y-4">
          {/* Driver Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {activeDriver.name}
              </h2>
              {activeDriver.description && (
                <p className="text-sm text-slate-500 mt-1">{activeDriver.description}</p>
              )}
            </div>
            <Badge variant="outline" className="text-sm">
              {getDriverProgress(activeDriverId).answered} of {getDriverProgress(activeDriverId).total} answered
            </Badge>
          </div>

          {/* Questions for this Driver */}
          <div className="space-y-6">
            {activeQuestions.map((question, qIndex) => {
              const skipped = isQuestionSkipped(question.id);
              const skipReasonText = getSkipReason(question.id);
              
              return (
              <Card key={question.id} className={`dashboard-card ${skipped ? 'border-slate-300 bg-slate-50/50' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {question.heading && (
                        <p className="text-xs font-semibold text-[#5AA2AE] uppercase tracking-wide mb-1">
                          {question.heading}
                        </p>
                      )}
                      <CardTitle className={`text-base leading-relaxed ${skipped ? 'text-slate-500' : ''}`}>
                        {question.text}
                        {question.required && !question.allow_skip && <span className="text-red-500 ml-1">*</span>}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {/* Skip button - only show if allow_skip is true */}
                      {question.allow_skip && !skipped && (
                        <Popover 
                          open={skipPopoverOpen[question.id] || false} 
                          onOpenChange={(open) => setSkipPopoverOpen(prev => ({ ...prev, [question.id]: open }))}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 h-7 px-2 text-xs"
                              data-testid={`skip-question-${question.id}`}
                            >
                              <SkipForward className="w-3 h-3 mr-1" />
                              Skip
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-2" align="end">
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-slate-700 px-2 py-1">Why are you skipping?</p>
                              {SKIP_REASONS.map((reason) => (
                                <button
                                  key={reason.value}
                                  onClick={() => handleSkipQuestion(question.id, reason.value)}
                                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 transition-colors"
                                  data-testid={`skip-reason-${reason.value}`}
                                >
                                  {reason.label}
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      <span className="text-xs text-slate-400">Q{qIndex + 1}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Skipped State Display */}
                  {skipped ? (
                    <div className="flex items-center justify-between p-4 bg-slate-100 rounded-lg">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Ban className="w-4 h-4" />
                        <span className="text-sm">Skipped: {skipReasonText}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnskipQuestion(question.id, question.question_type)}
                        className="text-[#5AA2AE] hover:text-[#5AA2AE]/80 hover:bg-[#5AA2AE]/10 text-xs"
                        data-testid={`unskip-question-${question.id}`}
                      >
                        Answer this question
                      </Button>
                    </div>
                  ) : (
                    <>
                  {/* Free Text */}
                  {question.question_type === 'free_text' && (
                    <Textarea
                      placeholder="Type your answer here..."
                      value={responses[question.id] || ''}
                      onChange={(e) => updateResponse(question.id, e.target.value)}
                      rows={3}
                      className="resize-none"
                    />
                  )}

                  {/* Slider */}
                  {question.question_type === 'slider' && (() => {
                    const config = question.slider_config || {};
                    const currentValue = responses[question.id];
                    const isUnanswered = currentValue === null || currentValue === undefined;
                    const minVal = config.min || config.min_value || 0;
                    const maxVal = config.max || config.max_value || 10;
                    const midVal = (minVal + maxVal) / 2;
                    // Use 100 steps across the range for fine-grained control
                    const stepSize = (maxVal - minVal) / 100;
                    
                    return (
                      <div className="space-y-4">
                        <div className="relative">
                          {isUnanswered ? (
                            // Empty slider track for unanswered state
                            <div 
                              className="relative flex w-full touch-none select-none items-center h-4 cursor-pointer"
                              onClick={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const percent = (e.clientX - rect.left) / rect.width;
                                // Calculate value with fine granularity
                                const rawValue = minVal + percent * (maxVal - minVal);
                                const value = Math.round(rawValue / stepSize) * stepSize;
                                updateResponse(question.id, Math.max(minVal, Math.min(maxVal, parseFloat(value.toFixed(2)))));
                              }}
                            >
                              <div className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-slate-200 border border-dashed border-slate-300">
                                {/* No filled range - empty track */}
                              </div>
                              <div className="absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-dashed border-slate-400 bg-white flex items-center justify-center animate-pulse">
                                <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                              </div>
                            </div>
                          ) : (
                            <Slider
                              value={[currentValue]}
                              onValueChange={([value]) => updateResponse(question.id, parseFloat(value.toFixed(2)))}
                              min={minVal}
                              max={maxVal}
                              step={stepSize}
                              className="w-full"
                            />
                          )}
                        </div>
                        <div className="flex justify-between text-sm text-slate-500">
                          <span style={config.left_color ? { color: config.left_color } : {}}>
                            {config.min_label || config.left_label || 'Low'}
                          </span>
                          <span style={config.middle_color ? { color: config.middle_color } : {}}>
                            {config.mid_label || config.middle_label || ''}
                          </span>
                          <span style={config.right_color ? { color: config.right_color } : {}}>
                            {config.max_label || config.right_label || 'High'}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Multiple Choice */}
                  {question.question_type === 'multiple_choice' && (
                    <RadioGroup
                      value={responses[question.id] || ''}
                      onValueChange={(value) => updateResponse(question.id, value)}
                      className="space-y-2"
                    >
                      {question.options?.map((option, index) => {
                        const optionText = typeof option === 'object' ? option.text : option;
                        const optionValue = typeof option === 'object' ? (option.id || option.text) : option;
                        return (
                          <div key={index} className="flex items-center space-x-3">
                            <RadioGroupItem value={optionValue} id={`${question.id}-option-${index}`} />
                            <Label htmlFor={`${question.id}-option-${index}`} className="font-normal cursor-pointer">
                              {optionText}
                            </Label>
                          </div>
                        );
                      })}
                    </RadioGroup>
                  )}

                  {/* Dropdown */}
                  {question.question_type === 'dropdown' && (
                    <Select
                      value={responses[question.id] || ''}
                      onValueChange={(value) => updateResponse(question.id, value)}
                    >
                      <SelectTrigger className="w-full max-w-sm">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {question.options?.map((option, index) => {
                          const optionText = typeof option === 'object' ? option.text : option;
                          const optionValue = typeof option === 'object' ? (option.id || option.text) : option;
                          return (
                            <SelectItem key={index} value={optionValue}>
                              {optionText}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Yes/No */}
                  {question.question_type === 'yes_no' && (
                    <RadioGroup
                      value={responses[question.id] || ''}
                      onValueChange={(value) => updateResponse(question.id, value)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id={`${question.id}-yes`} />
                        <Label htmlFor={`${question.id}-yes`} className="cursor-pointer">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id={`${question.id}-no`} />
                        <Label htmlFor={`${question.id}-no`} className="cursor-pointer">No</Label>
                      </div>
                    </RadioGroup>
                  )}

                  {/* Checkbox */}
                  {question.question_type === 'checkbox' && (
                    <div className="space-y-2">
                      {question.options?.map((option, index) => {
                        const optionText = typeof option === 'object' ? option.text : option;
                        const optionValue = typeof option === 'object' ? (option.id || option.text) : option;
                        return (
                          <div key={index} className="flex items-center space-x-3">
                            <Checkbox
                              id={`${question.id}-check-${index}`}
                              checked={(responses[question.id] || []).includes(optionValue)}
                              onCheckedChange={() => toggleCheckbox(question.id, optionValue)}
                            />
                            <Label htmlFor={`${question.id}-check-${index}`} className="font-normal cursor-pointer">
                              {optionText}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Multi-Topic */}
                  {question.question_type === 'multi_topic' && (
                    <div className="space-y-3">
                      <div 
                        className="space-y-2" 
                        data-topic-container
                        onTouchMove={(e) => handleTouchMove(e, question.id)}
                        onTouchEnd={handleTouchEnd}
                      >
                        {(responses[question.id] || []).map((topic, index) => (
                          <div
                            key={index}
                            data-topic-item
                            draggable
                            onDragStart={() => handleTopicDragStart(question.id, index)}
                            onDragOver={(e) => handleTopicDragOver(e, question.id, index)}
                            onDragEnd={handleTopicDragEnd}
                            onTouchStart={(e) => handleTouchStart(e, question.id, index)}
                            className={`flex items-center gap-2 p-2 bg-white border rounded-lg shadow-sm transition-all touch-none ${
                              dragState.questionId === question.id && dragState.index === index 
                                ? 'opacity-50 border-[#5AA2AE] scale-105' 
                                : 'border-slate-200'
                            }`}
                          >
                            <GripVertical className="w-4 h-4 text-slate-400 cursor-grab flex-shrink-0" />
                            <span className="w-5 h-5 rounded-full bg-[#5AA2AE] text-white text-xs flex items-center justify-center flex-shrink-0">
                              {index + 1}
                            </span>
                            <span className="flex-1 text-sm text-slate-700">{topic}</span>
                            <button
                              type="button"
                              onClick={() => removeTopic(question.id, index)}
                              className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="flex gap-2">
                        <Input
                          placeholder="Add a topic..."
                          value={topicInputs[question.id] || ''}
                          onChange={(e) => setTopicInputs(prev => ({ ...prev, [question.id]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addTopic(question.id);
                            }
                          }}
                          className="flex-1"
                        />
                        <Button type="button" onClick={() => addTopic(question.id)} variant="outline" size="sm">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>

                      {(responses[question.id] || []).length > 1 && (
                        <p className="text-xs text-slate-500">
                          Drag to reorder by priority (1 = highest)
                        </p>
                      )}
                    </div>
                  )}
                    </>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Auto-save indicator and Next Driver Button */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : lastSaved ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span>Auto-saved {lastSaved.toLocaleTimeString()}</span>
            </>
          ) : (
            <span>Responses auto-save as you type</span>
          )}
        </div>
        <Button
          onClick={handleNextDriver}
          disabled={saving}
          className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
          size="lg"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              {isLastDriver() ? 'Complete Survey' : 'Next Section'}
              <ChevronRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
